import React, { lazy, Suspense, Fragment } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import AuthLayout from 'src/layouts/AuthLayout';
import DashboardLayout from 'src/layouts/DashboardLayout';
import AdminLayout from 'src/layouts/AdminLayout';

import AuthGuard from 'src/components/AuthGuard';
import AdminGuard from 'src/components/AdminGuard';
import GuestGuard from 'src/components/GuestGuard';
import LoadingScreen from './components/LoadingScreen';

const routesConfig = [
  {
    exact: true,
    path: '/',
    component: () => <Redirect to="/login" />
  },
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/pages/Error404View'))
  },
  {
    path: '/forgot-password',
    guard: GuestGuard,
    layout: AuthLayout,
    routes: [
      {
        exact: true,
        path: '/forgot-password',
        component: lazy(() => import('src/views/auth/ForgotPasswordView'))
      },
      {
        exact: true,
        path: '/forgot-password/:token',
        component: lazy(() => import('src/views/auth/ForgotPasswordView'))
      },
    ]
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    layout: AuthLayout,
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login-direct',
    layout: AuthLayout,
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    path: '/login-unprotected',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    path: '/change-password',
    layout: AuthLayout,
    component: lazy(() => import('src/views/auth/ChangePasswordView'))
  },
  {
    exact: true,
    path: '/sms',
    layout: AuthLayout,
    component: lazy(() => import('src/views/auth/SMSView'))
  },
  {
    exact: true,
    guard: AuthGuard,
    path: '/search',
    layout: DashboardLayout,
    component: lazy(() => import('src/views/documents/DashboardView'))
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/app/documents/dashboard" />
      },
      {
        exact: true,
        path: '/app/documents/dashboard',
        component: lazy(() => import('src/views/documents/DashboardView'))
      },
      {
        exact: true,
        path: '/app/documents/list',
        component: lazy(() => import('src/views/documents/TableView'))
      },
      {
        exact: true,
        path: '/app/documents/:repoId/:id',
        component: lazy(() => import('src/views/documents/PreviewView'))
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '/admin',
    guard: AdminGuard,
    layout: AdminLayout,
    routes: [
      {
        exact: true,
        path: '/admin',
        component: () => <Redirect to="/admin/users" />
      },
      {
        exact: true,
        path: '/admin/users',
        component: lazy(() => import('src/views/admin/UserView'))
      },
      {
        exact: true,
        path: '/admin/roles',
        component: lazy(() => import('src/views/admin/RoleView'))
      },
      {
        exact: true,
        path: '/admin/repositories',
        component: lazy(() => import('src/views/admin/RepositoryView'))
      },
      {
        exact: true,
        path: '/admin/sites',
        component: lazy(() => import('src/views/admin/SiteView'))
      },
      {
        exact: true,
        path: '/admin/policies',
        component: lazy(() => import('src/views/admin/PolicyView'))
      },
      {
        exact: true,
        path: '/admin/audits',
        component: lazy(() => import('src/views/admin/AuditView')),
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '*',
    layout: DashboardLayout,
    routes: [
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

const renderRoutes = (routes) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, index) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;
        const key = index;

        return (
          <Route
            key={key}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Guard>
                <Layout>
                  {route.routes ? renderRoutes(route.routes) : <Component {...props} />}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

function Routes() {
  return renderRoutes(routesConfig);
}

export default Routes;
